import { FC, Fragment, useRef } from "react";
import { observer } from "mobx-react-lite";
import { SnippetChunk, TopicInfo } from "@/components/item-list/types";
import { isEmpty } from "lodash-es";
import styled from "@emotion/styled";
import { MDSPanelWidths } from "@/mds/foundations";
import { MDSCollectionChip, MDSIcon, MDSIconKind } from "@/mds";
import { HoverableCollectionChip } from "./hoverable-collection-chip";

export interface ItemListRowFeaturedContentProps {
  collections?: TopicInfo[];
  snippet?: SnippetChunk[];
  noteId?: string;
  templateId?: string;
  showAll?: boolean;
  isLoading?: boolean;
  inline?: boolean;
  compact?: boolean;
  onCollectionOverflowClick?: () => void;
}

export const ItemListRowFeaturedContent: FC<ItemListRowFeaturedContentProps> = observer(
  ({
    collections,
    snippet,
    noteId,
    templateId,
    showAll,
    isLoading,
    inline,
    onCollectionOverflowClick,
    compact = false,
  }) => {
    const activePopoverCloseRef = useRef<(() => void) | null>(null);
    const length = showAll ? collections?.length : 2;
    const anyCollections = !isEmpty(collections);
    const firstTwoCollections = collections?.slice(0, length);
    const remainingCollections = collections?.slice(length);
    const additionalCollectionsCount = remainingCollections?.length;
    const shouldShowAdditionalCollections = !!additionalCollectionsCount;

    if (isLoading) {
      return <LoaderContainer></LoaderContainer>;
    }
    if (!collections?.length && !snippet?.length) return null;

    return (
      <Container
        $wrap={showAll ?? false}
        $inline={inline}
        $flex={inline || (collections && !snippet)}
        $compact={compact}
      >
        {firstTwoCollections?.map(featuredCollection => {
          return (
            <HoverableCollectionChip
              key={featuredCollection.id}
              collection={featuredCollection}
              noteId={noteId}
              templateId={templateId}
              activePopoverCloseRef={activePopoverCloseRef}
            />
          );
        })}
        {shouldShowAdditionalCollections && (
          <MDSCollectionChip
            label={`+${additionalCollectionsCount}`}
            inline={inline}
            onClick={onCollectionOverflowClick}
          />
        )}
        {anyCollections && snippet && snippet.length > 0 && (
          <DotSeparatorIcon kind={MDSIconKind.Dot} size={4} />
        )}
        {snippet?.map(({ text, isHighlight, isTruncatedStart, isTruncatedEnd }, index) => (
          <Fragment key={index}>
            {isTruncatedStart && "…"}
            {isHighlight ? <HighlightedSnippet>{text}</HighlightedSnippet> : text}
            {isTruncatedEnd && "…"}
          </Fragment>
        ))}
      </Container>
    );
  }
);

const DotSeparatorIcon = styled(MDSIcon)`
  height: 100%;
  margin-right: 2px;
`;

const HighlightedSnippet = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

interface ContainerProps {
  $wrap: boolean;
  $inline?: boolean;
  $flex?: boolean;
  $compact?: boolean;
}

const Container = styled("div")<ContainerProps>`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: ${({ $compact, $inline, theme }) => (!$compact && $inline ? theme.spacing["1"] : 0)};

  @container mds-row-item (max-width: ${MDSPanelWidths.narrow}px) {
    margin-left: 0;
  }

  ${({ $compact, $flex, $wrap }) =>
    $flex &&
    `
      display: flex;
      flex-wrap: ${$wrap ? "wrap" : "nowrap"};
      gap: 4px;
      ${!$compact && "flex: 1 0;"}
    `}
`;

const LoaderContainer = styled.div({
  height: "16px",
});
